//src/hooks/useWorkerGroups.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addWorkerGroupMembers,
  createWorkerGroup,
  getWorkerGroup,
  listWorkerGroups,
  refreshWorkerGroup,
  removeWorkerGroupMember,
  setWorkerGroupStatus,
  updateWorkerGroup,
} from "@/services/workerGroups";
import type {
  WorkerGroupListParams,
  WorkerGroupMemberAddIn,
  WorkerGroupUpsertIn,
} from "@/types/gro";

export const WORKER_GROUPS_QK = (params: WorkerGroupListParams) =>
  ["worker_groups", params] as const;

export function useWorkerGroups(params: WorkerGroupListParams) {
  return useQuery({
    queryKey: WORKER_GROUPS_QK(params),
    queryFn: () => listWorkerGroups(params),
    staleTime: 20_000,
  });
}

export function useWorkerGroupDetail(id: string | undefined) {
  return useQuery({
    queryKey: ["worker_group_detail", id],
    queryFn: () => getWorkerGroup(id as string),
    enabled: !!id,
    staleTime: 20_000,
  });
}

export function useCreateWorkerGroup() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: WorkerGroupUpsertIn) => createWorkerGroup(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["worker_groups"] });
    },
  });
}

export function useUpdateWorkerGroup() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: WorkerGroupUpsertIn }) =>
      updateWorkerGroup(id, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["worker_groups"] });
      qc.invalidateQueries({ queryKey: ["worker_group_detail", variables.id] });
    },
  });
}

export function useSetWorkerGroupStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      setWorkerGroupStatus(id, { is_active }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["worker_groups"] });
      qc.invalidateQueries({ queryKey: ["worker_group_detail", variables.id] });
    },
  });
}

export function useRefreshWorkerGroup() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => refreshWorkerGroup(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["worker_groups"] });
      qc.invalidateQueries({ queryKey: ["worker_group_detail", id] });
    },
  });
}

export function useAddWorkerGroupMembers() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: WorkerGroupMemberAddIn }) =>
      addWorkerGroupMembers(id, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["worker_groups"] });
      qc.invalidateQueries({ queryKey: ["worker_group_detail", variables.id] });
    },
  });
}

export function useRemoveWorkerGroupMember() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      removeWorkerGroupMember(id, userId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["worker_groups"] });
      qc.invalidateQueries({ queryKey: ["worker_group_detail", variables.id] });
    },
  });
}
